/**
 * AI Chat Mention System - Core Singleton
 *
 * Central system for detecting and processing @ (data) and # (page) mentions
 * in AI chat input. Coordinates between DataMentionService and PageMentionService.
 */

import type {
  MentionContext,
  MentionSegment,
  MentionTrigger,
  ParsedMention,
  ResolvedMentionData,
} from './types';
import { MAX_DRILL_DEPTH } from './types';
import { DataMentionService } from './DataMentionService';
import { PageMentionService, pageMentionService } from './PageMentionService';
import { setRecentMentionsOrganization, clearAllRecentMentions } from './RecentMentions';

/** Mention enriched with surrounding context (lightweight replacement for removed SemanticExtractor) */
export interface MentionWithSemantics extends ParsedMention {
  surroundingContext: string;
  semantics?: {
    aggregation?: string;
    timeRange?: string;
    granularity?: string;
  };
}

// Regex for detecting mentions at cursor position
// Matches @ or # followed by:
//   - A double-quoted string (possibly incomplete): #"Page Name or #"Page Name"
//   - A single-quoted string (possibly incomplete): #'Page Name or #'Page Name'
//   - Alphanumeric/underscore/dash chars with optional dot-separated segments
// Negative lookbehind ensures we don't match escaped triggers (\@ or \#)
const MENTION_REGEX = /(?<!\\)([@#])(?:"([^"]*)"?|'([^']*)'?|([a-zA-Z0-9_-]*(?:\.[a-zA-Z0-9_-]*)*))$/;

// Regex for extracting all mentions from a complete message
// Supports quoted page names: #"Page Name" or #'Page Name'
const ALL_MENTIONS_REGEX = /(?<!\\)([@#])(?:"([^"]+)"|'([^']+)'|([a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*))/g;

export class MentionSystem {
  private static instance: MentionSystem;
  private dataMentionService: DataMentionService;
  private pageMentionService: PageMentionService;
  private recentMentions: MentionSegment[] = [];
  private readonly MAX_RECENT_MENTIONS = 5;

  private constructor() {
    this.dataMentionService = new DataMentionService();
    this.pageMentionService = pageMentionService;
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): MentionSystem {
    if (!MentionSystem.instance) {
      MentionSystem.instance = new MentionSystem();
    }
    return MentionSystem.instance;
  }

  /**
   * Detect if there's an active mention at the cursor position
   *
   * @param input - The full input string
   * @param cursorPosition - Current cursor position in the string
   * @returns MentionContext if a mention is detected, null otherwise
   *
   * @example
   * detectMention('@green', 6) // Returns context for '@green'
   * detectMention('@greenhouse.temp.', 18) // Returns context with path ['greenhouse', 'temp']
   * detectMention('\\@escaped', 9) // Returns null (escaped)
   * detectMention('Hello @user', 11) // Returns context for '@user' (mid-input)
   */
  public detectMention(input: string, cursorPosition: number): MentionContext | null {
    // Get the substring up to cursor position
    const textBeforeCursor = input.substring(0, cursorPosition);

    // Test for mention pattern
    const match = textBeforeCursor.match(MENTION_REGEX);

    if (!match) {
      return null;
    }

    const trigger = match[1] as MentionTrigger;
    const fullPath = match[2] || match[3] || match[4] || ''; // double-quoted, single-quoted, or unquoted
    const startPosition = textBeforeCursor.length - match[0].length;

    // Parse the path into segments
    const segments = fullPath.split('.').filter(Boolean);

    // Check depth limit - return null if path is too deep
    if (segments.length > MAX_DRILL_DEPTH) {
      return null;
    }

    // The last segment is the search term, everything before is the path
    const searchTerm = segments.length > 0 ? segments[segments.length - 1] : '';

    // If the input ends with a dot, the search term is empty and all segments are path
    const endsWithDot = fullPath.endsWith('.');
    const path = endsWithDot ? segments : segments.slice(0, -1);
    const finalSearchTerm = endsWithDot ? '' : searchTerm;

    return {
      trigger,
      type: trigger === '@' ? 'data' : 'page',
      searchTerm: finalSearchTerm,
      startPosition,
      endPosition: cursorPosition,
      path,
      rawText: match[0],
    };
  }

  /**
   * Get autocomplete suggestions for the current mention context
   *
   * @param context - The detected mention context
   * @returns Array of suggestion segments for the dropdown
   */
  public async getMentionSuggestions(context: MentionContext): Promise<MentionSegment[]> {
    if (context.type === 'data') {
      return this.dataMentionService.getSuggestions(context);
    } else {
      return this.pageMentionService.getSuggestions(context);
    }
  }

  /**
   * Parse all mentions from a submitted message
   *
   * @param message - The complete message text
   * @returns Array of parsed mentions found in the message
   *
   * @example
   * parseAllMentions('Show @greenhouse.temp data on #Dashboard')
   * // Returns [
   * //   { type: 'data', reference: 'greenhouse.temp', ... },
   * //   { type: 'page', reference: 'Dashboard', ... }
   * // ]
   */
  public parseAllMentions(message: string): ParsedMention[] {
    const mentions: ParsedMention[] = [];
    let match: RegExpExecArray | null;

    // Reset regex state
    ALL_MENTIONS_REGEX.lastIndex = 0;

    while ((match = ALL_MENTIONS_REGEX.exec(message)) !== null) {
      const trigger = match[1] as MentionTrigger;
      const reference = match[2] || match[3] || match[4]; // double-quoted, single-quoted, or unquoted

      mentions.push({
        type: trigger === '@' ? 'data' : 'page',
        reference,
        startPosition: match.index,
        endPosition: match.index + match[0].length,
        rawText: match[0],
      });
    }

    return mentions;
  }

  /**
   * Parse all mentions from a message and enrich with semantic context
   *
   * Uses SemanticExtractor to analyze the text surrounding each mention
   * and extract aggregation functions, time ranges, and granularity.
   *
   * @param message - The complete message text
   * @returns Array of parsed mentions enriched with semantic context
   *
   * @example
   * parseAllMentionsWithSemantics('Show average @greenhouse.temp for the last 24 hours')
   * // Returns [{
   * //   type: 'data',
   * //   reference: 'greenhouse.temp',
   * //   semantics: { aggregation: 'avg', timeRange: 'last_24h' },
   * //   surroundingContext: 'Show average @greenhouse.temp for the last 24 hours'
   * // }]
   */
  public parseAllMentionsWithSemantics(message: string): MentionWithSemantics[] {
    const mentions = this.parseAllMentions(message);
    return mentions.map(m => ({
      ...m,
      surroundingContext: message,
    }));
  }

  /**
   * Resolve a parsed mention to its full data
   *
   * @param mention - The parsed mention to resolve
   * @returns Resolved data or null if resolution fails
   */
  public async resolveMention(mention: ParsedMention): Promise<ResolvedMentionData | null> {
    if (mention.type === 'data') {
      return this.dataMentionService.resolveReference(mention.reference);
    } else {
      return this.pageMentionService.resolveReference(mention.reference);
    }
  }

  /**
   * Resolve all mentions in a message
   *
   * @param message - The message containing mentions
   * @returns Array of resolved mention data (null entries for failed resolutions)
   */
  public async resolveAllMentions(message: string): Promise<(ResolvedMentionData | null)[]> {
    const mentions = this.parseAllMentions(message);
    return Promise.all(mentions.map((m) => this.resolveMention(m)));
  }

  /**
   * Replace mention text in a message with resolved display text
   * Useful for showing resolved mentions in the UI
   *
   * @param message - Original message with raw mentions
   * @returns Message with mentions replaced by display text
   */
  public async formatMentionsForDisplay(message: string): Promise<string> {
    const mentions = this.parseAllMentions(message);
    if (mentions.length === 0) return message;

    // Resolve all mentions
    const resolved = await Promise.all(mentions.map((m) => this.resolveMention(m)));

    // Replace from end to start to preserve positions
    let result = message;
    for (let i = mentions.length - 1; i >= 0; i--) {
      const mention = mentions[i];
      const resolution = resolved[i];

      if (resolution) {
        let displayText = mention.rawText;

        if (resolution.type === 'data') {
          if (resolution.field) {
            displayText = `[${resolution.schema?.displayName || resolution.schema?.name}.${resolution.field.name}]`;
          } else if (resolution.schema) {
            displayText = `[${resolution.schema.displayName || resolution.schema.name}]`;
          } else if (resolution.group) {
            displayText = `[${resolution.group.name}]`;
          }
        } else if (resolution.type === 'page' && resolution.page) {
          displayText = `[${resolution.page.name}${resolution.page.isDefault ? ' (default)' : ''}]`;
        }

        result = result.substring(0, mention.startPosition) + displayText + result.substring(mention.endPosition);
      }
    }

    return result;
  }

  /**
   * Check if a message contains any mentions
   */
  public hasMentions(message: string): boolean {
    ALL_MENTIONS_REGEX.lastIndex = 0;
    return ALL_MENTIONS_REGEX.test(message);
  }

  /**
   * Escape a string to prevent mention detection
   * Useful for displaying literal @ or # characters
   */
  public escapeMention(text: string): string {
    return text.replace(/[@#]/g, '\\$&');
  }

  /**
   * Unescape previously escaped mentions
   */
  public unescapeMention(text: string): string {
    return text.replace(/\\([@#])/g, '$1');
  }

  /**
   * Clear all caches in mention services
   */
  public clearAllCaches(): void {
    this.dataMentionService.clearCache();
    this.pageMentionService.clearCache();
  }

  /**
   * Add a mention to recent history for quick re-selection
   */
  public addRecentMention(segment: MentionSegment): void {
    // Remove if already exists (will be re-added at front)
    this.recentMentions = this.recentMentions.filter(m => m.id !== segment.id);
    // Add to front
    this.recentMentions.unshift(segment);
    // Trim to max size
    if (this.recentMentions.length > this.MAX_RECENT_MENTIONS) {
      this.recentMentions.pop();
    }
  }

  /**
   * Get recently used mentions for quick access
   */
  public getRecentMentions(): MentionSegment[] {
    return [...this.recentMentions];
  }

  /**
   * Clear recent mentions history
   */
  public clearRecentMentions(): void {
    this.recentMentions = [];
  }

  /**
   * Set the current organization context for localStorage namespacing
   * Call this when organization context changes
   */
  public setOrganizationContext(orgId: string | null): void {
    setRecentMentionsOrganization(orgId);
  }

  /**
   * Clear all recent mentions (e.g., on logout)
   */
  public clearAllRecentMentions(): void {
    this.recentMentions = [];
    clearAllRecentMentions();
  }
}

// Export singleton instance
export const mentionSystem = MentionSystem.getInstance();
